import {Panel} from '@github-ui/docked-side-panel/Panel'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {CommentIcon, HistoryIcon} from '@primer/octicons-react'
import {Button, PortalContext, registerPortalRoot} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {clsx} from 'clsx'
import type {KeyboardEvent, ReactNode} from 'react'
import {Suspense, useCallback, useEffect, useId, useMemo, useRef} from 'react'
import {createPortal} from 'react-dom'

import {IssueTimelineErrorFallback} from '../IssueTimelineErrorFallback'
import {useActivityPanel} from './ActivityPanelContext'
import {ActivityLoadingState} from './ActivityTimelineControls'
import styles from './TimelineActivityMenu.module.css'

export function ActivityEmptyState() {
  return (
    <Blankslate>
      <Blankslate.Visual>
        <CommentIcon size={24} />
      </Blankslate.Visual>
      <Blankslate.Heading as="h4">No activity</Blankslate.Heading>
      <Blankslate.Description>Activity on this issue will appear here.</Blankslate.Description>
    </Blankslate>
  )
}

type TimelineActivityMenuProps = {
  initiallyOpen: boolean | null
  legacyHeader: boolean
  timeline: ReactNode
  /** Controls shown next to the panel heading, such as filter and sort menus. */
  headerActions?: ReactNode
  /** Shown above the timeline. */
  notice?: ReactNode
  /** Remounts the timeline when it changes, clearing an error it has shown. */
  timelineKey?: string
}

export function TimelineActivityMenu({
  initiallyOpen,
  legacyHeader,
  timeline,
  headerActions,
  notice,
  timelineKey,
}: TimelineActivityMenuProps) {
  const {closePanel, open, openPanel, panelTarget} = useActivityPanel()
  const initialOpenStateAppliedRef = useRef(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const portalContainerName = useId()
  const portalContext = useMemo(() => ({portalContainerName}), [portalContainerName])
  const registerActivityPortalRoot = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) registerPortalRoot(node, portalContainerName)
    },
    [portalContainerName],
  )

  useEffect(() => {
    if (initialOpenStateAppliedRef.current || initiallyOpen === null) return
    initialOpenStateAppliedRef.current = true
    if (initiallyOpen) openPanel()
  }, [initiallyOpen, openPanel])

  const handleOpen = useCallback(() => {
    openPanel()
    sendEvent('issue_viewer.activity_panel.opened')
  }, [openPanel])

  const handleClose = useCallback(() => {
    if (!open) return
    closePanel()
    sendEvent('issue_viewer.activity_panel.closed')
  }, [closePanel, open])

  const handleDockedKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      // Handle Escape before the containing issue side panel dismisses the issue.
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key !== 'Escape' || event.defaultPrevented) return
      const target = event.target
      if (target instanceof Element) {
        if (target.closest('[role="menu"]')) return
        const dialog = target.closest('[role="dialog"]')
        if (dialog && !dialog.contains(closeButtonRef.current)) return
      }
      event.preventDefault()
      event.stopPropagation()
      handleClose()
      requestAnimationFrame(() => buttonRef.current?.focus())
    },
    [handleClose],
  )

  const panelContent = (
    <>
      {notice}
      <ErrorBoundary key={timelineKey} boundaryName="issue-activity-timeline" fallback={<IssueTimelineErrorFallback />}>
        <Suspense fallback={<ActivityLoadingState />}>{timeline}</Suspense>
      </ErrorBoundary>
    </>
  )
  const actions = headerActions ? <div className={styles.headerActions}>{headerActions}</div> : null

  return (
    <>
      <Button ref={buttonRef} leadingVisual={HistoryIcon} onClick={handleOpen}>
        Activity
      </Button>
      {open &&
        panelTarget &&
        createPortal(
          <PortalContext value={portalContext}>
            <div className="height-full" onKeyDownCapture={handleDockedKeyDown}>
              <Panel
                heading="Activity"
                headingLevel={2}
                headerActions={actions}
                closeButtonRef={closeButtonRef}
                returnFocusRef={buttonRef}
                onClose={handleClose}
                headerClassName={clsx(styles.dockedHeader, legacyHeader && styles.legacyDockedHeader)}
              >
                <Panel.Header>
                  <div ref={registerActivityPortalRoot} />
                </Panel.Header>
                <Panel.Body className={styles.body}>{panelContent}</Panel.Body>
              </Panel>
            </div>
          </PortalContext>,
          panelTarget,
        )}
    </>
  )
}
